import { AbilityFactory, AppAbility } from '@bcba/casl-authorization';
import { ExplorerCurrentUserSerializer } from '@bcba/serializers';
import { USER_ROLES, VALIDATION_STATUSES, ValidationStatus } from '@bcba/types';
import { Center, Spinner } from '@chakra-ui/react';
import {
  QueryObserverResult,
  RefetchOptions,
  useQuery,
} from '@tanstack/react-query';
import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';
import { getCurrentUser } from '../services/users';

type UserContextType = {
  user: ExplorerCurrentUserSerializer;
  isLoadingUser: boolean;
  signUpRequestStatus: ValidationStatus;
  isSignUpRequestApproved: boolean;
  ability?: AppAbility;
  refetchUser: (
    options?: RefetchOptions | undefined,
  ) => Promise<QueryObserverResult<ExplorerCurrentUserSerializer, Error>>;
  userHasPendingChanges: boolean;
  setUserHasPendingChanges: React.Dispatch<React.SetStateAction<boolean>>;
};

const UserContext = createContext<UserContextType | undefined>(undefined);

type UserProviderProps = {
  children: ReactNode;
};

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const [ability, setAbility] = useState<AppAbility>();
  const [userHasPendingChanges, setUserHasPendingChanges] = useState(false);
  const {
    data: user,
    isLoading: isLoadingUser,
    isSuccess,
    refetch: refetchUser,
  } = useQuery({
    queryKey: ['current-user'],
    queryFn: getCurrentUser,
  });

  const isSignUpRequestApproved =
    user?.signUpRequestStatus === VALIDATION_STATUSES.APPROVED;

  useEffect(() => {
    if (!isLoadingUser && user) {
      setAbility(
        new AbilityFactory().createForUser(USER_ROLES.CLIENT, {
          userUuid: user.uuid,
          companyUuid: user.company?.uuid,
        }),
      );
    }
  }, [isLoadingUser, user, isSignUpRequestApproved]);

  if ((!user && isLoadingUser) || !isSuccess || !ability)
    return (
      <Center height="100vh" data-testid="isLoadingUser-loader">
        <Spinner />
      </Center>
    );

  const value: UserContextType = {
    user,
    isLoadingUser,
    signUpRequestStatus: user.signUpRequestStatus,
    isSignUpRequestApproved,
    ability,
    refetchUser,
    userHasPendingChanges,
    setUserHasPendingChanges,
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export const useUserContext = (): UserContextType => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUserContext must be used within a UserProvider');
  }
  return context;
};
